import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { IResponse } from '@shared/models/base/http-response.model';
import { Job, StartDownloadRequest } from '@shared/models/download-job.model';
import { FileDownloadService } from '@shared/services/file-download.service';

@Injectable({
  providedIn: 'root',
})
export class DownloadJobsService {
  private readonly http = inject(HttpClient);
  private readonly fileDownloadService = inject(FileDownloadService);

  getJobs(inProgressOnly = false): Observable<IResponse<Job[]>> {
    const endpoint = new URL('/Job/GetUserGenerateFileJobs', environment.apiUrl);
    endpoint.searchParams.set('inProgressOnly', String(inProgressOnly));

    return this.http.get<IResponse<Job[]>>(endpoint.href);
  }

  /** Queues a generation job. The API answers with a translation key, not the job itself. */
  startDownload(request: StartDownloadRequest): Observable<IResponse<string>> {
    return this.http.post<IResponse<string>>(
      new URL('/Job/QueueGenerateFileJob', environment.apiUrl).href,
      request,
    );
  }

  getJobFile(jobId: string): Promise<IResponse<Blob>> {
    return this.fileDownloadService.getFileData(this.getJobFileEndpoint(jobId));
  }

  downloadJob(jobId: string, suggestedFileName = 'download'): Promise<void> {
    return this.fileDownloadService.downloadFile(
      this.getJobFileEndpoint(jobId),
      suggestedFileName,
    );
  }

  private getJobFileEndpoint(jobId: string): string {
    if (!jobId.trim()) {
      throw new Error('A job ID is required to access the file.');
    }

    const endpoint = new URL('/Job/GetGeneratedJobFile', environment.apiUrl);
    endpoint.searchParams.set('jobId', jobId);
    return endpoint.href;
  }
}
